/**
 * Transport section: grouped type cards with expandable sub-items + directions dialog
 *
 * Uses event delegation on each .transport-container so click priority is:
 *   1. "+ X more" toggle  →  expand sub-list
 *   2. Sub-item with coords  →  directions (detail) or fly-to-hub (listing)
 *   3. Card with coords (not expanded)  →  directions (detail) or fly-to-hub (listing)
 *   4. "More transport options" button  →  reveal hidden type cards
 */

import { isTouch } from '../utils/tap-to-activate.js';

import { getMap, showTransportOnMap, openTransportPopup } from './maps.js';

export function initTransport() {
    const isListing = !!document.querySelector('.office-listing');
    const isGuide = document.body.id === 'guides';

    let previewedCard = null;

    function clearPreview() {
        if (previewedCard) {
            previewedCard.classList.remove('transport-card-previewing');
            previewedCard = null;
        }
    }

    // Tap outside clears preview
    if (isTouch) {
        document.addEventListener('click', (e) => {
            if (previewedCard && !previewedCard.contains(e.target)) {
                clearPreview();
            }
        });
    }

    document.querySelectorAll('.transport-container').forEach(container => {
        // Set pointer cursor on clickable cards
        container.querySelectorAll('.transport-card[data-dest-lat][data-dest-lng]').forEach(card => {
            card.style.cursor = 'pointer';
        });

        /**
         * Handle transport card action (directions or fly-to-hub).
         * On touch devices: first tap shows preview overlay, second tap opens.
         * On desktop: click opens immediately (hover already shows overlay).
         */
        function handleCardAction(element, getName, getLat, getLng) {
            // Sub-items in expanded cards don't need preview — act immediately
            const parentCard = element.closest('.transport-card');
            const isExpanded = parentCard?.classList.contains('transport-card-expanded');

            if (isTouch && !isExpanded && previewedCard !== parentCard) {
                clearPreview();
                previewedCard = parentCard || element;
                previewedCard.classList.add('transport-card-previewing');
                return;
            }

            clearPreview();

            if (isListing) {
                flyToHub(parseFloat(getLat()), parseFloat(getLng()));
            } else if (isGuide) {
                showLocation(getLat(), getLng(), getName());
            } else {
                showDirections(
                    container.dataset.officeLat,
                    container.dataset.officeLng,
                    getLat(),
                    getLng(),
                    getName()
                );
            }
        }

        container.addEventListener('click', (e) => {
            // 1. "+ X more" toggle — expand sub-list within the card
            const toggle = e.target.closest('[data-transport-toggle]');
            if (toggle) {
                expandCard(toggle, container);
                return;
            }

            // 2. Sub-item click (after expansion) — directions or fly-to-hub
            const subItem = e.target.closest('.transport-sub-item[data-dest-lat][data-dest-lng]');
            if (subItem) {
                handleCardAction(
                    subItem,
                    () => subItem.querySelector('.transport-sub-name')?.textContent || 'Destination',
                    () => subItem.dataset.destLat,
                    () => subItem.dataset.destLng
                );
                return;
            }

            // 3. Whole-card click (not expanded) — directions or fly-to-hub
            const card = e.target.closest('.transport-card[data-dest-lat][data-dest-lng]');
            if (card && !card.classList.contains('transport-card-expanded')) {
                handleCardAction(
                    card,
                    () => card.querySelector('.transport-name')?.textContent || 'Destination',
                    () => card.dataset.destLat,
                    () => card.dataset.destLng
                );
                return;
            }

            // 4. "More transport options" button — reveal hidden type cards
            const expandBtn = e.target.closest('[data-transport-expand]');
            if (expandBtn) {
                container.querySelectorAll('.transport-card-hidden').forEach(c => {
                    c.classList.add('transport-card-visible');
                });
                expandBtn.remove();
            }
        });
    });
}

/**
 * Expand a grouped card to show all sub-items.
 * Locks the info container height, swaps primary row for the full sub-list
 * (whose first item replaces the primary with distance + icon),
 * then smoothly animates height and fades in the sub-list.
 */
function expandCard(toggleBtn, container) {
    const card = toggleBtn.closest('.transport-card');
    const subList = card.querySelector('.transport-sub-list');
    if (!subList) return;

    const primaryRow = card.querySelector('.transport-primary-row');
    const infoEl = card.querySelector('.transport-info');

    // 1. Lock container to current height and anchor content to top
    //    (justify-content: center causes sub-list to float above container)
    const startHeight = infoEl.offsetHeight;
    infoEl.style.height = startHeight + 'px';
    infoEl.style.overflow = 'hidden';
    infoEl.style.justifyContent = 'flex-start';

    // 2. Replace primary name with summary, hide toggle + distance, show sub-list
    const nameEl = primaryRow?.querySelector('.transport-name');
    if (nameEl) {
        const items = subList.querySelectorAll('.transport-sub-item');
        const typeLabel = card.querySelector('.transport-label')?.textContent.trim() || '';
        const lastDist = items.length ? items[items.length - 1].querySelector('.transport-sub-distance')?.textContent.trim() : '';
        const typePlural = { BUS: 'bus stops', RAILWAY: 'stations', AIRPORT: 'airports',
            SUBWAY: 'stations', FERRY: 'terminals', TRAM: 'tram stops' }[typeLabel.toUpperCase()] || 'stops';
        nameEl.textContent = `${items.length} ${typePlural}` + (lastDist ? ` within ${lastDist}` : '');
    }
    subList.hidden = false;
    subList.style.opacity = '0';
    card.querySelectorAll('.transport-info > .transport-distance').forEach(el => el.style.display = 'none');
    toggleBtn.hidden = true;

    // 3. Add expanded class now so padding-right is included in measurement
    card.classList.add('transport-card-expanded');

    // 4. Measure target height with final layout applied
    infoEl.style.height = 'auto';
    const endHeight = infoEl.offsetHeight;
    infoEl.style.height = startHeight + 'px';

    // 5. Animate height + fade in sub-list
    infoEl.style.transition = 'height 0.35s ease';
    subList.style.transition = 'opacity 0.3s ease 0.08s';
    card.offsetHeight;
    infoEl.style.height = endHeight + 'px';
    subList.style.opacity = '1';

    // 6. Clean up on transition end (not setTimeout — guarantees correct timing)
    infoEl.addEventListener('transitionend', function cleanup(e) {
        if (e.propertyName !== 'height') return;
        infoEl.removeEventListener('transitionend', cleanup);
        infoEl.style.height = '';
        infoEl.style.overflow = '';
        infoEl.style.transition = '';
        infoEl.style.justifyContent = '';
        subList.style.transition = '';
        subList.style.opacity = '';
    });

    // Mark sub-items with coords as clickable
    card.querySelectorAll('.transport-sub-item[data-dest-lat]').forEach(item => {
        item.style.cursor = 'pointer';
    });
}

/**
 * Open location dialog with a pin on the map (no directions — used on guide pages)
 */
function showLocation(destLat, destLng, destName) {
    if (!destLat || !destLng) return;

    const mapsUrl = `https://maps.google.com/maps?output=embed&q=${destLat},${destLng}+(${encodeURIComponent(destName)})&hl=en&z=14`;

    const dialog = document.getElementById('directions-dialog');
    if (!dialog) return;

    const title = dialog.querySelector('.dialog-header h3');
    if (title) title.textContent = destName;

    const dialogContent = dialog.querySelector('.dialog-content');
    let iframe = dialog.querySelector('iframe');
    if (!iframe) {
        dialogContent.innerHTML = '';
        iframe = document.createElement('iframe');
        iframe.setAttribute('loading', 'lazy');
        iframe.setAttribute('allowfullscreen', '');
        dialogContent.appendChild(iframe);
    }

    iframe.src = mapsUrl;
    dialog.showModal();
}

/**
 * Open directions dialog with Google Maps embed
 */
function showDirections(officeLat, officeLng, destLat, destLng, destName) {
    if (!officeLat || !destLat) return;

    const mapsUrl = `https://maps.google.com/maps?output=embed&saddr=${officeLat},${officeLng}&daddr=${destLat},${destLng}+(${encodeURIComponent(destName)})&hl=en`;

    const dialog = document.getElementById('directions-dialog');
    if (!dialog) return;

    const title = dialog.querySelector('.dialog-header h3');
    if (title) title.textContent = `Directions to ${destName}`;

    const dialogContent = dialog.querySelector('.dialog-content');
    let iframe = dialog.querySelector('iframe');
    if (!iframe) {
        dialogContent.innerHTML = '';
        iframe = document.createElement('iframe');
        iframe.setAttribute('loading', 'lazy');
        iframe.setAttribute('allowfullscreen', '');
        dialogContent.appendChild(iframe);
    }

    iframe.src = mapsUrl;
    dialog.showModal();
}

/**
 * Fly the listing map to show a transport hub in context with the search area.
 * Swaps office markers for transport markers so the hub icon is visible,
 * then builds bounds from the map's current centre + hub coords.
 */
function flyToHub(lat, lng) {
    const map = getMap();
    if (!map || isNaN(lat) || isNaN(lng)) return;

    // Scroll map into view so the user sees the result
    const mapEl = document.getElementById('office-map');
    if (mapEl) mapEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    map.invalidateSize();

    // Swap to transport layer so the hub icon is visible
    showTransportOnMap();

    // Fit bounds from current centre → hub so hub is shown in relation to the town
    const centre = map.getCenter();
    const bounds = L.latLngBounds([centre, [lat, lng]]);
    map.flyToBounds(bounds, {
        padding: [40, 40],
        duration: 0.8,
        maxZoom: 14
    });

    // Open the popup on the matching marker once the fly animation finishes
    map.once('moveend', () => openTransportPopup(lat, lng));
}
